/**
 * Agrupamento das tags (por escopo e por categoria) e o filtro facetado (PLANO_TAGS D7)
 * — o mesmo do backend, para as telas que filtram no cliente sobre a lista inteira
 * (processos, offline-first).
 */
import type { Tag, TagCategoria, TagEquipe, TagResumo } from '@/api/tags';

export interface GrupoCategoria {
  /** Nula = "Sem categoria". */
  categoria: TagCategoria | null;
  tags: Tag[];
}

export interface SecaoEscopo {
  chave: string;
  titulo: string;
  /** Nulo = tags pessoais. */
  escritorioId: string | null;
  /** Preenchido = as tags da equipe, no escritório dela. */
  equipeId: string | null;
  grupos: GrupoCategoria[];
}

const porNome = (a: { nome: string }, b: { nome: string }) =>
  a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });

/**
 * Grupos por categoria, na `ordem` delas, com "Sem categoria" por último.
 * `incluirVazias`: o diálogo mostra a categoria sem tag (para criar tag nela); o
 * filtro não.
 */
export function agruparPorCategoria(
  tags: Tag[],
  categorias: TagCategoria[],
  { incluirVazias = false } = {},
): GrupoCategoria[] {
  const ordenadas = [...categorias].sort((a, b) => a.ordem - b.ordem || porNome(a, b));
  const ids = new Set(ordenadas.map((c) => c.id));
  const grupos: GrupoCategoria[] = ordenadas.map((categoria) => ({
    categoria,
    tags: tags.filter((t) => t.categoria_id === categoria.id).sort(porNome),
  }));
  const semCategoria = tags.filter((t) => !t.categoria_id || !ids.has(t.categoria_id)).sort(porNome);
  const resultado = incluirVazias ? grupos : grupos.filter((g) => g.tags.length > 0);
  if (semCategoria.length > 0) resultado.push({ categoria: null, tags: semCategoria });
  return resultado;
}

/**
 * Seções "Minhas", uma por escritório (só quando há escritório) e, depois de cada
 * escritório, uma por equipe dele que a pessoa vê; cada uma agrupada por categoria. A tag
 * da equipe usa as categorias do escritório, que só aparecem vazias na seção do escritório.
 */
export function agruparPorEscopo(
  tags: Tag[],
  categorias: TagCategoria[],
  escritorios: { id: string; nome: string }[],
  opcoes: { incluirVazias?: boolean; tituloPessoal?: string; equipes?: TagEquipe[] } = {},
): SecaoEscopo[] {
  // A tag de uma equipe que não veio em `equipes` (quem chama sem elas) fica no escritório:
  // nunca some de todas as seções.
  const comSecao = new Set((opcoes.equipes ?? []).map((e) => e.id));
  const daEquipe = (t: Tag) => !!t.equipe_id && comSecao.has(t.equipe_id);
  const secoes: SecaoEscopo[] = [
    {
      chave: 'pessoal',
      titulo: opcoes.tituloPessoal ?? 'Minhas',
      escritorioId: null,
      equipeId: null,
      grupos: agruparPorCategoria(
        tags.filter((t) => !t.escritorio_id),
        categorias.filter((c) => !c.escritorio_id),
        opcoes,
      ),
    },
  ];
  for (const escritorio of escritorios) {
    secoes.push({
      chave: `escritorio:${escritorio.id}`,
      titulo: escritorios.length > 1 ? `Do escritório ${escritorio.nome}` : 'Do escritório',
      escritorioId: escritorio.id,
      equipeId: null,
      grupos: agruparPorCategoria(
        tags.filter((t) => t.escritorio_id === escritorio.id && !daEquipe(t)),
        categorias.filter((c) => c.escritorio_id === escritorio.id),
        opcoes,
      ),
    });
    for (const equipe of (opcoes.equipes ?? []).filter((e) => e.escritorio_id === escritorio.id)) {
      secoes.push({
        chave: `equipe:${equipe.id}`,
        titulo: `Da equipe ${equipe.nome}`,
        escritorioId: escritorio.id,
        equipeId: equipe.id,
        grupos: agruparPorCategoria(
          tags.filter((t) => t.equipe_id === equipe.id),
          categorias.filter((c) => c.escritorio_id === escritorio.id),
        ),
      });
    }
  }
  return secoes;
}

/**
 * Facetas do filtro: tags da mesma categoria formam uma faceta (OU); categorias
 * diferentes e cada tag sem categoria são facetas separadas (E). Tag que não está
 * em `tagsPorId` (excluída, ou que a pessoa não vê) é ignorada; se nenhuma sobra,
 * o filtro não casa nada — nunca vira "sem filtro".
 */
export function facetasDoFiltro(filtroIds: string[], tagsPorId: Map<string, TagResumo>): string[][] | null {
  if (filtroIds.length === 0) return null;
  const porCategoria = new Map<string, string[]>();
  const avulsas: string[][] = [];
  for (const id of filtroIds) {
    const tag = tagsPorId.get(id);
    if (!tag) continue;
    if (tag.categoria_id) {
      porCategoria.set(tag.categoria_id, [...(porCategoria.get(tag.categoria_id) ?? []), id]);
    } else {
      avulsas.push([id]);
    }
  }
  const facetas = [...porCategoria.values(), ...avulsas];
  return facetas.length > 0 ? facetas : [[]];
}

/** O registro (com estas tags) passa no filtro? `facetas` nulo = sem filtro. */
export function casaFacetas(tagsDoRegistro: TagResumo[] | undefined, facetas: string[][] | null): boolean {
  if (!facetas) return true;
  const ids = new Set((tagsDoRegistro ?? []).map((t) => t.id));
  return facetas.every((faceta) => faceta.some((id) => ids.has(id)));
}
